import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../api";
import { downloadBlob } from "../nativeDownload";

// "closed" and "full" are computed server-side (effective_status/is_full),
// never stored -- an event can be simultaneously Open and Full (dinner-only
// responses still allowed), so Full is its own badge, not a status.
const STATUS_STYLE = {
  draft:     { bg: "#f3f4f6", color: "#4b5563", label: "Draft" },
  open:      { bg: "#dcfce7", color: "#166534", label: "Open" },
  closed:    { bg: "#fef3c7", color: "#92400e", label: "Closed" },
  current:   { bg: "#dbeafe", color: "#1e40af", label: "Current Competition" },
  completed: { bg: "#ede9fe", color: "#5b21b6", label: "Completed" },
};

const DEFAULT_INVITE_SUBJECT = "⛳ {{event_name}} — are you coming?";
const DEFAULT_INVITE_BODY =
`Hi {{first_name}},

Our next fixture is {{event_name}} on {{date}} at {{venue}}.

Cost: {{golf_cost}}  Buggy: {{buggy_cost}}
Please let us know by {{closing_date}}.

Sign up: {{link_signup}}
Sign up with a buggy: {{link_buggy}}
Can't make it: {{link_decline}}

Thanks!`;
const MERGE_TAGS = ["first_name","name","event_name","date","venue","golf_cost","dinner_cost","buggy_cost","closing_date","link_signup","link_buggy","link_decline"];

const BASE_TABS = ["Players", "Reserves", "Costs", "Tee Times", "Communications"];

const PAYMENT_STATUS_STYLE = {
  UNPAID:         { bg: "#f3f4f6", color: "#4b5563", label: "Unpaid" },
  PENDING:        { bg: "#fef3c7", color: "#92400e", label: "Pending" },
  PART_PAID:      { bg: "#fef9c3", color: "#854d0e", label: "Part Paid" },
  PAID:           { bg: "#dcfce7", color: "#166534", label: "Paid" },
  FAILED:         { bg: "#fee2e2", color: "#991b1b", label: "Failed" },
  CANCELLED:      { bg: "#f3f4f6", color: "#6b7280", label: "Cancelled" },
  PART_REFUNDED:  { bg: "#fce7f3", color: "#9d174d", label: "Part Refunded" },
  REFUNDED:       { bg: "#fce7f3", color: "#9d174d", label: "Refunded" },
};

export default function PlannerEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Players");
  const [toast, setToast] = useState("");
  const [confirmMakeCurrent, setConfirmMakeCurrent] = useState(false);
  const [makingCurrent, setMakingCurrent] = useState(false);

  const [inviteFilter, setInviteFilter] = useState("all");
  const [inviteSubject, setInviteSubject] = useState(DEFAULT_INVITE_SUBJECT);
  const [inviteBody, setInviteBody] = useState(DEFAULT_INVITE_BODY);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [allPlayers, setAllPlayers] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [addPlayerId, setAddPlayerId] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [confirmDeleteFixture, setConfirmDeleteFixture] = useState(false);
  const [deletingFixture, setDeletingFixture] = useState(false);
  const [updatingPlayerId, setUpdatingPlayerId] = useState(null);

  const [payments, setPayments] = useState(null);
  const [recordModal, setRecordModal] = useState(null); // { payment_id, name, balance }
  const [recordAmount, setRecordAmount] = useState("");
  const [recordMethod, setRecordMethod] = useState("cash");
  const [recordReference, setRecordReference] = useState("");
  const [recording, setRecording] = useState(false);
  const [refundModal, setRefundModal] = useState(null); // { payment_id, name, amount_paid }
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  const [showAddCostItem, setShowAddCostItem] = useState(false);
  const [newItem, setNewItem] = useState({ description: "", category: "Golf", quantity: "1", unit_cost: "", charge_to: "all_confirmed", cost_basis: "per_player" });
  const [addingItem, setAddingItem] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [detailModal, setDetailModal] = useState(null); // { payment_id, name, reference, ... }
  const [detailTransactions, setDetailTransactions] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustTarget, setAdjustTarget] = useState("due");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [confirmingTxId, setConfirmingTxId] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/planner/${id}`);
      setData(res.data);
    } catch(e) { console.error(e); showToast("Couldn't load this fixture"); }
    finally { setLoading(false); }
  }, [id]);

  const loadPayments = useCallback(async () => {
    try { const res = await api.get(`/planner/${id}/payments`); setPayments(res.data); }
    catch(e) { console.error(e); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/players").then(r => setAllPlayers(r.data)).catch(() => {}); }, []);
  // Always loaded, not just when payments_required -- this is also the
  // source of cost items / Event Cost / Player Charges for the Costs tab,
  // which must keep working even on a fixture with payments fully off.
  useEffect(() => { if (data) loadPayments(); }, [data, loadPayments]);

  if (loading) return <Layout><div style={{padding:"20px",color:"#6b7280",fontSize:"13px"}}>Loading…</div></Layout>;
  if (!data) return <Layout><div style={{padding:"20px",color:"#991b1b",fontSize:"13px"}}>Fixture not found.</div></Layout>;

  const { fixture, confirmed, reserve, declined } = data;
  const st = STATUS_STYLE[fixture.effective_status] || STATUS_STYLE.draft;
  const placesLeft = fixture.max_players != null ? Math.max(0, fixture.max_players - confirmed.filter(p => p.golf).length) : null;

  const publish = async () => {
    try { await api.post(`/planner/${id}/publish`); showToast("Published — members can now sign up"); load(); }
    catch(e) { showToast(e.response?.data?.error || "Couldn't publish"); }
  };

  const promote = async (attendanceId) => {
    try { await api.post(`/planner/${id}/promote/${attendanceId}`); showToast("Promoted to confirmed"); load(); }
    catch(e) { showToast(e.response?.data?.error || "Couldn't promote"); }
  };

  // Manually register a response on a player's behalf -- for members who
  // phone/text the organiser instead of using the public page, Player
  // Portal, or an email link. Same /respond endpoint those use, just
  // organiser-authed so it works even after the closing date.
  const addPlayerManually = async (choice, buggy) => {
    if (!addPlayerId) return;
    setAddingPlayer(true);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: parseInt(addPlayerId, 10), choice, buggy: !!buggy });
      showToast("Response recorded");
      setShowAddPlayer(false);
      setAddPlayerId("");
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't record that response"); }
    finally { setAddingPlayer(false); }
  };

  // Same gap as the member-facing signup flows had (see the "+ Add Buggy"
  // fix there) -- the Players list itself was pure display, so a confirmed
  // player with no buggy had no way to get one added after the fact other
  // than re-running the whole Add Player flow. Reuses the same /respond
  // endpoint, just toggling buggy while keeping them signed up.
  const updateConfirmedPlayer = async (playerId, buggy) => {
    setUpdatingPlayerId(playerId);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: playerId, choice: "signup", buggy });
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't update that player"); }
    finally { setUpdatingPlayerId(null); }
  };

  // "Remove" = decline, same as a member's own "Can't make it" -- keeps
  // them visible in the Declined list below rather than erasing any
  // record they were ever confirmed, and automatically frees their place
  // for the reserve list exactly like a real cancellation would.
  const removePlayer = async (playerId, name) => {
    setUpdatingPlayerId(playerId);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: playerId, choice: "decline" });
      showToast(`${name} removed`);
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't remove that player"); }
    finally { setUpdatingPlayerId(null); }
  };

  const makeCurrent = async () => {
    setMakingCurrent(true);
    try {
      const res = await api.post(`/planner/${id}/make-current`);
      showToast(`Done — ${res.data.transferred} player(s) transferred to the live competition`);
      setConfirmMakeCurrent(false);
      load();
    } catch(e) {
      showToast(e.response?.data?.error || "Couldn't make this the current competition");
      setConfirmMakeCurrent(false);
      load(); // a 409 (already linked) means the UI was stale -- refresh so the "Go to Competition" button appears
    }
    finally { setMakingCurrent(false); }
  };

  const sendInvite = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await api.post(`/planner/${id}/invite`, { subject: inviteSubject, body: inviteBody, filter: inviteFilter });
      setSendResult(res.data);
      showToast(`Sent to ${res.data.sent.length} player(s)`);
    } catch(e) { showToast("Send failed"); }
    finally { setSending(false); }
  };

  const computedTeeTime = (i) => {
    const [h, m] = (fixture.first_tee_time || "09:00").split(":").map(Number);
    const total = h * 60 + m + i * (fixture.tee_interval_minutes || 8);
    const hh = Math.floor(total / 60) % 24, mm = total % 60;
    return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
  };
  const teeTimeFor = (i) => (fixture.tee_time_overrides && fixture.tee_time_overrides[i]) || computedTeeTime(i);
  const saveTeeTimeOverride = async (i, value) => {
    try { await api.put(`/planner/${id}/teetimes`, { overrides: { [i]: value } }); load(); }
    catch(e) { showToast("Couldn't save that tee time"); }
  };

  const addCostItem = async () => {
    if (!newItem.description.trim() || !newItem.unit_cost) { showToast("Description and unit cost are required"); return; }
    setAddingItem(true);
    try {
      await api.post(`/planner/${id}/cost-items`, { ...newItem, quantity: parseFloat(newItem.quantity) || 1, unit_cost: parseFloat(newItem.unit_cost) });
      setNewItem({ description: "", category: "Golf", quantity: "1", unit_cost: "", charge_to: "all_confirmed", cost_basis: "per_player" });
      setShowAddCostItem(false);
      loadPayments();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't add that cost item"); }
    finally { setAddingItem(false); }
  };

  const deleteCostItem = async (itemId) => {
    setDeletingItemId(itemId);
    try { await api.delete(`/planner/${id}/cost-items/${itemId}`); loadPayments(); }
    catch(e) { showToast(e.response?.data?.error || "Couldn't remove that item"); }
    finally { setDeletingItemId(null); }
  };

  const exportPayments = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/planner/${id}/payments/export`, { responseType: "blob" });
      await downloadBlob(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${fixture.event_name.replace(/[^a-z0-9]+/gi, "_")}_payments.xlsx`);
    } catch(e) { showToast("Couldn't export"); }
    finally { setExporting(false); }
  };

  const openDetail = async (row) => {
    setDetailModal(row);
    setDetailTransactions(null);
    try {
      const res = await api.get(`/planner/${id}/payments/${row.payment_id}/transactions`);
      setDetailTransactions(res.data);
    } catch(e) { showToast("Couldn't load transaction history"); }
  };

  const confirmReconciliation = async (txId) => {
    setConfirmingTxId(txId);
    try {
      await api.post(`/planner/${id}/payments/${detailModal.payment_id}/transactions/${txId}/confirm`);
      const res = await api.get(`/planner/${id}/payments/${detailModal.payment_id}/transactions`);
      setDetailTransactions(res.data);
    } catch(e) { showToast(e.response?.data?.error || "Couldn't confirm"); }
    finally { setConfirmingTxId(null); }
  };

  const submitAdjustment = async () => {
    if (!adjustAmount || !adjustReason.trim()) { showToast("Amount and reason are both required"); return; }
    setAdjusting(true);
    try {
      await api.post(`/planner/${id}/payments/${detailModal.payment_id}/adjust`, {
        amount: parseFloat(adjustAmount), target: adjustTarget, reason: adjustReason,
      });
      showToast("Adjustment recorded");
      setAdjustAmount(""); setAdjustReason("");
      const res = await api.get(`/planner/${id}/payments/${detailModal.payment_id}/transactions`);
      setDetailTransactions(res.data);
      loadPayments();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't record that adjustment"); }
    finally { setAdjusting(false); }
  };

  const recordPayment = async () => {
    if (!recordModal) return;
    setRecording(true);
    try {
      await api.post(`/planner/${id}/payments/${recordModal.payment_id}/record`, {
        amount: parseFloat(recordAmount), method: recordMethod, reference: recordReference || undefined,
        date: new Date().toISOString().slice(0, 10),
      });
      showToast("Payment recorded");
      setRecordModal(null); setRecordAmount(""); setRecordReference("");
      loadPayments();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't record that payment"); }
    finally { setRecording(false); }
  };

  const issueRefund = async () => {
    if (!refundModal) return;
    setRefunding(true);
    try {
      await api.post(`/planner/${id}/payments/${refundModal.payment_id}/refund`, {
        amount: parseFloat(refundAmount), reason: refundReason || undefined,
      });
      showToast("Refund recorded");
      setRefundModal(null); setRefundAmount(""); setRefundReason("");
      loadPayments();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't record that refund"); }
    finally { setRefunding(false); }
  };

  const deleteFixture = async () => {
    setDeletingFixture(true);
    try {
      await api.delete(`/planner/${id}`);
      navigate("/planner");
    } catch(e) { showToast(e.response?.data?.error || "Couldn't delete this fixture"); setDeletingFixture(false); }
  };
  const golfConfirmed = confirmed.filter(p => p.golf);
  const respondedIds = new Set([...confirmed, ...reserve, ...declined].map(p => p.player_id));
  const addablePlayers = allPlayers.filter(p => !respondedIds.has(p.id)).sort((a,b) => (a.name||'').localeCompare(b.name||''));

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-3">
          <button onClick={() => navigate("/planner")} className="text-xs text-gray-500 hover:text-gray-700">← Planner</button>
          <span className="font-semibold text-sm">{fixture.event_name}</span>
          <span style={{background:st.bg,color:st.color,fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>{st.label.toUpperCase()}</span>
          {fixture.is_full && <span style={{background:"#fee2e2",color:"#991b1b",fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>FULL</span>}
          {reserve.length > 0 && <span style={{background:"#fef3c7",color:"#92400e",fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>{reserve.length} RESERVE{reserve.length !== 1 ? "S" : ""}</span>}
          <div className="ml-auto flex items-center gap-2">
            {fixture.status === "draft" && (
              <button onClick={publish} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Publish</button>
            )}
            {fixture.status === "open" && (
              <button onClick={() => setConfirmMakeCurrent(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">▶ Make Current Competition</button>
            )}
            {fixture.status === "current" && (
              <button onClick={() => navigate("/players")} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">✅ Linked to Competition #{fixture.created_event_id} — Go to Competition →</button>
            )}
            <button onClick={() => setConfirmDeleteFixture(true)}
              style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"6px",padding:"6px 12px",fontSize:"12px",fontWeight:700,color:"#b91c1c",cursor:"pointer"}}>
              🗑️ Delete
            </button>
          </div>
        </div>

        <div style={{padding:"14px 20px",background:"white",borderBottom:"1px solid #e5e7eb",display:"flex",gap:"24px",fontSize:"13px",color:"#374151"}}>
          <div>📅 {new Date(fixture.event_date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
          <div>📍 {fixture.venue || "no venue set"}</div>
          <div>⛳ {golfConfirmed.length} confirmed{placesLeft != null && ` · ${placesLeft} places left`}</div>
          {fixture.closing_date && (
            <div>{fixture.effective_status === "closed" ? "Closed" : "Closes"} {new Date(fixture.closing_date).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
          )}
        </div>

        <div style={{display:"flex",gap:"4px",background:"white",borderBottom:"1px solid #e5e7eb",padding:"0 16px"}}>
          {(fixture.payments_required ? [...BASE_TABS, "Payments"] : BASE_TABS).map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{padding:"10px 14px",fontSize:"13px",fontWeight:tab===t?700:500,color:tab===t?"#14532d":"#6b7280",borderBottom:tab===t?"2px solid #14532d":"2px solid transparent",background:"none",border:"none",borderBottomWidth:"2px",cursor:"pointer"}}>
              {t}{t==="Reserves" && reserve.length>0 ? ` (${reserve.length})` : ""}
              {t==="Payments" && payments && payments.totals.outstanding > 0 ? ` (£${payments.totals.outstanding.toFixed(2)} due)` : ""}
            </button>
          ))}
        </div>

        <div style={{padding:"20px",overflow:"auto",flex:1,background:"#f9fafb"}}>
          {tab === "Players" && (
            <div>
              <div style={{marginBottom:"14px"}}>
                {!showAddPlayer ? (
                  <button onClick={() => setShowAddPlayer(true)} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">+ Add Player</button>
                ) : (
                  <div style={{background:"white",borderRadius:"8px",padding:"14px",border:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap"}}>
                    <select value={addPlayerId} onChange={e => setAddPlayerId(e.target.value)}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,minWidth:"200px"}}>
                      <option value="">Select a player…</option>
                      {addablePlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("signup", false)}
                      style={{background:"#166534",color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Sign Up
                    </button>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("signup", true)}
                      style={{background:"#1d4ed8",color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Buggy Required
                    </button>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("decline", false)}
                      style={{background:"none",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Can't make it
                    </button>
                    <button onClick={() => { setShowAddPlayer(false); setAddPlayerId(""); }}
                      style={{background:"none",color:"#9ca3af",border:"none",fontSize:12,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {confirmed.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No confirmed players yet.</div>}
              {confirmed.map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:"white",borderRadius:"8px",marginBottom:"6px",border:"1px solid #e5e7eb"}}>
                  <div style={{flex:1,fontWeight:600,fontSize:"13px"}}>{p.name}</div>
                  <div style={{fontSize:"12px",color:"#6b7280",display:"flex",alignItems:"center",gap:"10px"}}>
                    {p.golf && <span>⛳ Signed Up</span>}
                    {p.buggy ? (
                      <span>🚗 Buggy</span>
                    ) : p.golf && (
                      <button onClick={() => updateConfirmedPlayer(p.player_id, true)} disabled={updatingPlayerId === p.player_id}
                        style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",color:"#1d4ed8",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"3px 8px"}}>
                        + Add Buggy
                      </button>
                    )}
                    <button onClick={() => removePlayer(p.player_id, p.name)} disabled={updatingPlayerId === p.player_id}
                      style={{background:"none",border:"1px solid #fecaca",borderRadius:"6px",color:"#b91c1c",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"3px 8px"}}>
                      ✕ Remove
                    </button>
                  </div>
                </div>
              ))}
              {declined.length > 0 && (
                <>
                  <div style={{fontSize:"12px",fontWeight:700,color:"#9ca3af",margin:"18px 0 6px"}}>Declined</div>
                  {declined.map(p => (
                    <div key={p.id} style={{padding:"8px 14px",fontSize:"13px",color:"#9ca3af"}}>{p.name}</div>
                  ))}
                </>
              )}
            </div>
          )}

          {tab === "Reserves" && (
            <div>
              {reserve.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No one on the reserve list.</div>}
              {reserve.map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:"white",borderRadius:"8px",marginBottom:"6px",border:"1px solid #fde68a"}}>
                  <div style={{fontSize:"12px",fontWeight:700,color:"#92400e",width:"24px"}}>#{p.reserve_position}</div>
                  <div style={{flex:1,fontWeight:600,fontSize:"13px"}}>{p.name}</div>
                  <button onClick={() => promote(p.id)} className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Promote to Confirmed</button>
                </div>
              ))}
            </div>
          )}

          {tab === "Costs" && (
            <div style={{maxWidth:"620px"}}>
              <div style={{display:"flex",gap:"12px",marginBottom:"16px",flexWrap:"wrap"}}>
                {[["Event Cost", payments?.totals?.event_cost, "#374151"],["Player Charges", payments?.totals?.expected, "#166534"],["Club Contribution", payments?.totals?.club_contribution, "#92400e"]].map(([label, val, color]) => (
                  <div key={label} style={{background:"white",border:"1px solid #e5e7eb",borderRadius:"10px",padding:"12px 16px",minWidth:"130px"}}>
                    <div style={{fontSize:"11px",color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>{label}</div>
                    <div style={{fontSize:"18px",fontWeight:800,color}}>£{(val ?? 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>

              <div style={{background:"white",borderRadius:"10px",padding:"16px",border:"1px solid #e5e7eb"}}>
                {(!payments || payments.cost_items.length === 0) && <div style={{color:"#9ca3af",fontSize:"13px",marginBottom:"12px"}}>No cost items yet.</div>}
                {payments && payments.cost_items.map(item => {
                  const billable = item.charge_to === "all_confirmed" || item.charge_to === "buggy_only";
                  return (
                  <div key={item.id} style={{display:"flex",alignItems:"center",gap:"10px",padding:"8px 0",borderBottom:"1px solid #f3f4f6",fontSize:"13px"}}>
                    <div style={{flex:1}}>
                      <span style={{fontWeight:600}}>{item.description}</span>
                      <span style={{color:"#9ca3af"}}>
                        {" "}· {item.category} ·{" "}
                        {billable && item.cost_basis === "fixed"
                          ? `£${Number(item.unit_cost).toFixed(2)} fixed, split across the group`
                          : `${Number(item.quantity)} × £${Number(item.unit_cost).toFixed(2)}`}
                      </span>
                    </div>
                    <span style={{fontSize:"11px",color:"#6b7280",background:"#f3f4f6",borderRadius:"999px",padding:"3px 8px"}}>
                      {({all_confirmed:"All Confirmed", buggy_only:"Buggy Players", club:"Club/Society", info_only:"Info Only"})[item.charge_to]}
                    </span>
                    <span style={{fontWeight:700,minWidth:"70px",textAlign:"right"}}>£{(Number(item.quantity) * Number(item.unit_cost)).toFixed(2)}</span>
                    <button onClick={() => deleteCostItem(item.id)} disabled={deletingItemId===item.id}
                      style={{background:"none",border:"none",color:"#b91c1c",cursor:"pointer",fontSize:"13px"}}>✕</button>
                  </div>
                  );
                })}

                {!showAddCostItem ? (
                  <button onClick={() => setShowAddCostItem(true)} style={{marginTop:"12px"}} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">+ Add Cost Item</button>
                ) : (
                  <div style={{marginTop:"12px",display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:"8px"}}>
                    <input placeholder="Description (e.g. Green Fees)" value={newItem.description} onChange={e => setNewItem(n => ({...n, description: e.target.value}))}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,gridColumn:"1 / -1"}} />
                    <select value={newItem.category} onChange={e => setNewItem(n => ({...n, category: e.target.value}))}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}}>
                      {["Golf","Food & Drink","Buggy","Prizes","Venue","Administration","Other"].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" step="0.01" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(n => ({...n, quantity: e.target.value}))}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}} />
                    <input type="number" step="0.01" placeholder="Unit cost £" value={newItem.unit_cost} onChange={e => setNewItem(n => ({...n, unit_cost: e.target.value}))}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}} />
                    <select value={newItem.charge_to} onChange={e => setNewItem(n => ({...n, charge_to: e.target.value}))}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}}>
                      <option value="all_confirmed">Charge to: All confirmed players</option>
                      <option value="buggy_only">Charge to: Players with a buggy</option>
                      <option value="club">Charge to: Club/Society (not billed)</option>
                      <option value="info_only">Info only (not counted)</option>
                    </select>
                    {(newItem.charge_to === "all_confirmed" || newItem.charge_to === "buggy_only") && (
                      <select value={newItem.cost_basis} onChange={e => setNewItem(n => ({...n, cost_basis: e.target.value}))}
                        style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}}>
                        <option value="per_player">Per player (unit cost = each person's charge)</option>
                        <option value="fixed">Fixed total, split across the group</option>
                      </select>
                    )}
                    <div style={{display:"flex",gap:"8px"}}>
                      <button onClick={addCostItem} disabled={addingItem} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded" style={{opacity:addingItem?0.5:1}}>Add</button>
                      <button onClick={() => setShowAddCostItem(false)} style={{background:"none",color:"#9ca3af",border:"none",fontSize:12,cursor:"pointer"}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"12px"}}>
                Event Cost is the budgeted total (quantity × unit cost). Player Charges is what's actually billed to confirmed players right now — it updates as real signups come in and can differ from the budget. Club Contribution is the gap the society itself covers.
              </div>
            </div>
          )}

          {tab === "Payments" && (
            <div style={{maxWidth:"720px"}}>
              {!payments ? (
                <div style={{color:"#9ca3af",fontSize:"13px"}}>Loading…</div>
              ) : (
                <>
                  <div style={{display:"flex",gap:"12px",marginBottom:"16px",flexWrap:"wrap",alignItems:"center"}}>
                    {[["Expected", payments.totals.expected, "#374151"],["Received", payments.totals.received, "#166534"],
                      ["Outstanding", payments.totals.outstanding, "#92400e"],["Refunded", payments.totals.refunded, "#9d174d"]].map(([label, val, color]) => (
                      <div key={label} style={{background:"white",border:"1px solid #e5e7eb",borderRadius:"10px",padding:"12px 16px",minWidth:"120px"}}>
                        <div style={{fontSize:"11px",color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>{label}</div>
                        <div style={{fontSize:"18px",fontWeight:800,color}}>£{val.toFixed(2)}</div>
                      </div>
                    ))}
                    <button onClick={exportPayments} disabled={exporting}
                      style={{marginLeft:"auto",background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"8px",color:"#1d4ed8",fontSize:"12px",fontWeight:700,cursor:"pointer",padding:"8px 14px",opacity:exporting?0.5:1}}>
                      {exporting ? "Exporting…" : "⬇ Export to Excel"}
                    </button>
                  </div>
                  <div style={{display:"flex",gap:"6px",marginBottom:"12px",flexWrap:"wrap"}}>
                    {[["all","All"],["UNPAID","Unpaid"],["PART_PAID","Part Paid"],["PAID","Paid"],["REFUNDED","Refunded"]].map(([val,label]) => (
                      <button key={val} onClick={() => setPaymentFilter(val)}
                        style={{padding:"4px 12px",fontSize:"12px",fontWeight:700,borderRadius:"999px",border:paymentFilter===val?"1px solid #166534":"1px solid #d1d5db",background:paymentFilter===val?"#dcfce7":"white",color:paymentFilter===val?"#166534":"#6b7280",cursor:"pointer"}}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {payments.rows.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No confirmed players yet.</div>}
                  {payments.rows.filter(r => paymentFilter === "all" || r.status === paymentFilter).map(r => {
                    const ps = PAYMENT_STATUS_STYLE[r.status] || PAYMENT_STATUS_STYLE.UNPAID;
                    const overdue = fixture.payment_deadline && new Date() > new Date(fixture.payment_deadline) && r.balance > 0;
                    return (
                      <div key={r.payment_id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:"white",borderRadius:"8px",marginBottom:"6px",border:"1px solid #e5e7eb",opacity:r.cancelled?0.7:1}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:600,fontSize:"13px"}}>{r.name}{r.cancelled && <span style={{color:"#b91c1c",fontWeight:700,fontSize:"11px"}}> (cancelled)</span>}</div>
                          <div style={{fontSize:"12px",color:"#6b7280"}}>
                            {r.reference} · Due £{r.amount_due.toFixed(2)} · Paid £{r.amount_paid.toFixed(2)} ·{" "}
                            {r.balance < 0 ? <span style={{color:"#1d4ed8",fontWeight:700}}>Overpaid by £{Math.abs(r.balance).toFixed(2)}</span> : `Balance £${r.balance.toFixed(2)}`}
                          </div>
                        </div>
                        {overdue && <span style={{background:"#fee2e2",color:"#991b1b",fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>⚠ Overdue</span>}
                        <span style={{background:ps.bg,color:ps.color,fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>{ps.label}</span>
                        <button onClick={() => openDetail(r)}
                          style={{background:"none",border:"1px solid #d1d5db",borderRadius:"6px",color:"#6b7280",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"5px 10px"}}>
                          Details
                        </button>
                        {r.balance > 0 && (
                          <button onClick={() => { setRecordModal({ payment_id: r.payment_id, name: r.name, balance: r.balance }); setRecordAmount(r.balance.toFixed(2)); }}
                            style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",color:"#1d4ed8",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"5px 10px"}}>
                            Record Payment
                          </button>
                        )}
                        {r.amount_paid > 0 && !["REFUNDED"].includes(r.status) && (
                          <button onClick={() => { setRefundModal({ payment_id: r.payment_id, name: r.name, amount_paid: r.amount_paid }); setRefundAmount(r.amount_paid.toFixed(2)); }}
                            style={{background:"none",border:"1px solid #fbcfe8",borderRadius:"6px",color:"#9d174d",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"5px 10px"}}>
                            Refund
                          </button>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {tab === "Tee Times" && (
            <div style={{background:"white",borderRadius:"10px",padding:"20px",border:"1px solid #e5e7eb",maxWidth:"360px"}}>
              <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"12px"}}>First tee {fixture.first_tee_time}, every {fixture.tee_interval_minutes} mins — click a time to adjust it if the club's given you an odd gap. Informational until this fixture is made current.</div>
              {golfConfirmed.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No golf signups yet.</div>}
              {Array.from({length: Math.ceil(golfConfirmed.length / 4)}).map((_, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f3f4f6",fontSize:"13px"}}>
                  <input type="time" value={teeTimeFor(i)} onChange={e => saveTeeTimeOverride(i, e.target.value)}
                    style={{fontWeight:700,border:"1px solid transparent",borderRadius:6,padding:"2px 4px",fontSize:13,fontFamily:"inherit"}}
                    onFocus={e => e.target.style.border="1px solid #d1d5db"} onBlur={e => e.target.style.border="1px solid transparent"} />
                  <span style={{color:"#6b7280"}}>{golfConfirmed.slice(i*4,i*4+4).map(p=>p.name).join(", ")}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "Communications" && (
            <div style={{background:"white",borderRadius:"10px",padding:"20px",border:"1px solid #e5e7eb",maxWidth:"640px"}}>
              <div style={{display:"flex",gap:"8px",marginBottom:"16px",flexWrap:"wrap"}}>
                {[["all","Invite Members"],["non_responders","Email Non-Responders"],["confirmed","Email Confirmed Players"],["reserve","Email Reserve List"]].map(([val,label]) => (
                  <button key={val} onClick={() => setInviteFilter(val)}
                    style={{padding:"6px 12px",fontSize:"12px",fontWeight:700,borderRadius:"999px",border:inviteFilter===val?"1px solid #166534":"1px solid #d1d5db",background:inviteFilter===val?"#dcfce7":"white",color:inviteFilter===val?"#166534":"#6b7280",cursor:"pointer"}}>
                    {label}
                  </button>
                ))}
              </div>
              <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Subject</label>
              <input value={inviteSubject} onChange={e => setInviteSubject(e.target.value)}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"12px"}} />
              <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Body</label>
              <textarea value={inviteBody} onChange={e => setInviteBody(e.target.value)} rows={10}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",fontFamily:"inherit"}} />
              <div style={{fontSize:12,color:"#6b7280",margin:"8px 0 16px"}}>
                Merge tags: {MERGE_TAGS.map(t => <code key={t} style={{background:"#eef2ff",color:"#3730a3",borderRadius:4,padding:"1px 5px",marginRight:6,fontSize:12}}>{`{{${t}}}`}</code>)}
              </div>
              <button onClick={sendInvite} disabled={sending}
                style={{background:"#6b21a8",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:sending?0.5:1}}>
                {sending ? "Sending…" : "Send"}
              </button>
              {sendResult && (
                <div style={{marginTop:"12px",fontSize:"12px",color:"#374151"}}>
                  Sent to {sendResult.sent.length}. {sendResult.skipped.length > 0 && <>Skipped (no email / failed): {sendResult.skipped.join(", ")}</>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {confirmMakeCurrent && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"440px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#1e40af",marginBottom:"8px"}}>▶ Make "{fixture.event_name}" the Current Competition?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This creates a real competition from this fixture and ticks every confirmed golfer as Playing on the Players screen ({golfConfirmed.length} player(s)). Dinner-only signups won't be ticked. Any other active competition will be deactivated. This can't be undone from here — you'd need to edit the new competition by hand afterward.
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmMakeCurrent(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={makeCurrent} disabled={makingCurrent}
                style={{background:"#1e40af",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:makingCurrent?0.5:1}}>
                {makingCurrent ? "Working…" : "Yes, Make Current"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteFixture && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"400px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Delete "{fixture.event_name}"?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This removes the fixture and every signup against it ({confirmed.length + reserve.length + declined.length} response(s)). This can't be undone.
              {fixture.status === "current" && (
                <><br/><br/><strong>Note:</strong> this fixture is linked to Competition #{fixture.created_event_id}. Deleting it here only removes the Planner record — the real competition, its Players/Groups/Scores, are not affected and must be deleted separately from Players if you want those gone too.</>
              )}
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmDeleteFixture(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={deleteFixture} disabled={deletingFixture}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:deletingFixture?0.5:1}}>
                {deletingFixture ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {recordModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"380px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#166534",marginBottom:"8px"}}>Record Payment — {recordModal.name}</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>Balance owed: £{recordModal.balance.toFixed(2)}. This records money received outside GSM (cash, cheque, bank transfer) — it doesn't take payment itself.</div>
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Amount (£)</label>
            <input type="number" step="0.01" value={recordAmount} onChange={e => setRecordAmount(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"12px"}} />
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Method</label>
            <select value={recordMethod} onChange={e => setRecordMethod(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"12px"}}>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Reference / notes (optional)</label>
            <input value={recordReference} onChange={e => setRecordReference(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"16px"}} />
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setRecordModal(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={recordPayment} disabled={recording || !recordAmount}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:(recording||!recordAmount)?0.5:1}}>
                {recording ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {refundModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"380px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#9d174d",marginBottom:"8px"}}>Refund — {refundModal.name}</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>Amount paid so far: £{refundModal.amount_paid.toFixed(2)}. This records a refund already made outside GSM — it doesn't move money itself. The original payment stays on record either way.</div>
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Refund amount (£)</label>
            <input type="number" step="0.01" value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"12px"}} />
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Reason (optional)</label>
            <input value={refundReason} onChange={e => setRefundReason(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"16px"}} />
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setRefundModal(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={issueRefund} disabled={refunding || !refundAmount}
                style={{background:"#9d174d",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:(refunding||!refundAmount)?0.5:1}}>
                {refunding ? "Saving…" : "Confirm Refund"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"460px",width:"100%",maxHeight:"80vh",overflow:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#14532d",marginBottom:"4px"}}>{detailModal.name}</div>
            <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"16px"}}>{detailModal.reference}</div>

            <div style={{fontSize:"13px",fontWeight:700,color:"#374151",marginBottom:"8px"}}>Transaction History</div>
            {!detailTransactions ? (
              <div style={{color:"#9ca3af",fontSize:"13px"}}>Loading…</div>
            ) : detailTransactions.length === 0 ? (
              <div style={{color:"#9ca3af",fontSize:"13px",marginBottom:"16px"}}>No transactions yet.</div>
            ) : (
              <div style={{marginBottom:"16px"}}>
                {detailTransactions.map(t => (
                  <div key={t.id} style={{padding:"8px 0",borderBottom:"1px solid #f3f4f6",fontSize:"12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontWeight:700}}>{t.transaction_type}</span>
                      <span>£{Number(t.amount).toFixed(2)}</span>
                    </div>
                    <div style={{color:"#9ca3af",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span>{new Date(t.created_at).toLocaleString("en-GB")}{t.notes ? ` — ${t.notes}` : ""}</span>
                      {t.transaction_type === "OFFLINE_PAYMENT" && (
                        t.reconciliation_status === "confirmed" ? (
                          <span style={{color:"#166534",fontWeight:700}}>✓ Confirmed</span>
                        ) : (
                          <button onClick={() => confirmReconciliation(t.id)} disabled={confirmingTxId===t.id}
                            style={{background:"none",border:"1px solid #d1d5db",borderRadius:"6px",color:"#6b7280",fontSize:"11px",cursor:"pointer",padding:"2px 8px"}}>
                            Mark Confirmed
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{fontSize:"13px",fontWeight:700,color:"#374151",marginBottom:"8px",borderTop:"1px solid #e5e7eb",paddingTop:"12px"}}>Make an Adjustment</div>
            <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"8px"}}>The only sanctioned way to change figures after payment has started — always needs a reason, always leaves its own record.</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"8px"}}>
              <input type="number" step="0.01" placeholder="Amount (+ or -)" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}} />
              <select value={adjustTarget} onChange={e => setAdjustTarget(e.target.value)}
                style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13}}>
                <option value="due">Adjust amount due</option>
                <option value="paid">Adjust amount paid</option>
              </select>
            </div>
            <input placeholder="Reason (required)" value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
              style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,boxSizing:"border-box",marginBottom:"12px"}} />

            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setDetailModal(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Close
              </button>
              <button onClick={submitAdjustment} disabled={adjusting || !adjustAmount || !adjustReason.trim()}
                style={{background:"#374151",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:(adjusting||!adjustAmount||!adjustReason.trim())?0.5:1}}>
                {adjusting ? "Saving…" : "Apply Adjustment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
